"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { ProductCardSSRContent } from "@/components/ProductCard/innerContent";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import { cn } from "@/lib/utils";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  FormatSubType,
  FormatType,
  NYI_NUMBER,
  NYI_STRING,
} from "@/constants/tagular/main";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import useProductEvents from "@/hooks/eventing/useProductEvents";
import { useInView } from "react-intersection-observer";
import { ProductCardProps } from "@/components/ProductCard/types";
import { isElementHidden } from "@/utils/isElementHidden";
import { bindProductCardClickedEvent as bindSegmentProductCardClickedEvent } from "@/lib/segment";
import useWebview from "@/webview/useWebview";
import ProductCardWrapper from "./ProductCardWrapper";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import { appendAlgoliaQueryParamsToHref } from "@/utils/functions/appendAlgoliaQueryParamsToHref";
import useMarketplaceAttributionHref from "@/hooks/useMarketplaceAttributionHref";

export function ClientSideProductCard({ onClick, ...props }: ProductCardProps) {
  const {
    partnerKey,
    name,
    href,
    location,
    tagular_variant,
    subjects,
    index = -1,
    formatType = FormatType.ProductCard,
    formatSubtype = FormatSubType.Grid,
    skipViewEvent = false,
    queryId,
    uuid,
    listId,
    searchQueryId,
    resultsIndex,
    tagularCustomDimensions,
    tagularWebContext,
  } = props;
  const { getUrl } = useWebview();
  const tagularProduct: ProductClicked["product"] = {
    brand: getBrandStringFromPartnerKeys(
      Array.isArray(partnerKey) ? partnerKey : [partnerKey ?? ""],
    ),
    formatSubtype,
    formatType,
    location: location || NYI_STRING,
    name: name,
    variant: tagular_variant || translateVariant(props.type),
    position: index < 0 ? NYI_NUMBER : index + 1,
    category: getTagularSubject(subjects),
    productId: uuid,
  };

  const customDimensions: ProductClicked["customDimensions"] =
    tagularCustomDimensions ??
    createCustomDimensions([
      ["level", props.level],
      ["courseCount", props.childCoursesCount],
      ["pacing", props.pacing],
      ["isPartOfProgram", props.isPartOfProgram],
      ["learningType", props.type],
      ["weeksToComplete", props.weeksToComplete],
      ["searchQueryId", searchQueryId],
    ]);

  const tagularEventOverrides = tagularWebContext
    ? { webContext: tagularWebContext }
    : {};

  const { productClicked, productViewed, correlationId } = useProductEvents({
    product: tagularProduct,
    customDimensions,
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setCorrelationId(correlationId);

    productClicked({
      listId,
      ...tagularEventOverrides,
    });

    onClick?.(e);
  };

  const ref = useRef<HTMLAnchorElement>(undefined);

  const {
    ref: inViewRef,
    inView,
    entry,
  } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const setRefs = useCallback(
    (node: HTMLAnchorElement) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  const hrefWithQueryParams = queryId
    ? appendAlgoliaQueryParamsToHref({
        href,
        index: resultsIndex || PRODUCT_INDEX_EN,
        queryId,
        position: (index < 0 ? NYI_NUMBER : index + 1).toString(),
      })
    : href;
  const attributedHref = useMarketplaceAttributionHref(hrefWithQueryParams);

  useEffect(() => {
    if (!inView) {
      return;
    }

    bindSegmentProductCardClickedEvent({
      domElement: ref.current as Element,
      link: attributedHref,
      label: props.name,
      objectId: props.objectId,
      queryId: props?.queryId,
      index: props.resultsIndex,
      productSource: props.productSource,
      product: props.product,
      productCategory: props.type ?? "",
      resultLevel: props.resultLevel,
      uuid: props.uuid,
      title: props.parentTitle,
      activeRunKey: props.activeRunKey,
      position: (props.index ?? 0) + 1,
    });

    if (
      !skipViewEvent &&
      entry &&
      !isElementHidden(entry.target as HTMLElement)
    ) {
      productViewed({
        listId,
        ...tagularEventOverrides,
      });
    }
  }, [inView, listId, searchQueryId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <a
      ref={setRefs}
      onClick={handleClick}
      href={getUrl(attributedHref)}
      className={cn(
        "no-underline hover:no-underline h-[332px] w-full max-w-[330px] mx-auto md:mx-0",
        props.className,
      )}
    >
      <ProductCardSSRContent {...props} />
    </a>
  );
}

export default function ProductCard(props: ProductCardProps) {
  return <ProductCardWrapper {...props} />;
}
